import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import policyService from '../../services/PolicyService';
import leaveService from '../../services/LeaveService';

export const fetchLeavePolicies = createAsyncThunk(
  'leavePolicy/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await policyService.GetLeavePolicyList();

      console.log("[fetchLeavePolicies] Response:", response);

      if (response?.StatusCode !== 200) {
        throw new Error(response?.Message || "Failed to retrieve leave policies.");
      }

      const result = response?.ResultSet || [];

      // Group policies by employee type name
      const groupedPolicies = {};

      result.forEach((item) => {
        const employeeTypeName = item.HEC_CATEGORY_NAME || 'EXECUTIVE';

        if (!groupedPolicies[employeeTypeName]) {
          groupedPolicies[employeeTypeName] = [];
        }

        groupedPolicies[employeeTypeName].push({
          id: item.HLP_POLICY_ID,
          leaveTypeId: item.HLP_LEAVE_TYPE_ID,
          leaveType: item.LEAVE_TYPE_NAME || '',
          annualEarn: Number(item.HLP_ANNUAL_ALLOCATION) || 0,
          monthlyEarn: Number(item.HLP_MONTHLY_EARN) || 0,
          quarter1: Number(item.HLP_QUARTER1) || 0,
          quarter2: Number(item.HLP_QUARTER2) || 0,
          quarter3: Number(item.HLP_QUARTER3) || 0,
          quarter4: Number(item.HLP_QUARTER4) || 0,
          carry: item.HLP_CARRY_FORWARD === 'Y' ? 'Yes' : 'No',
          maxCarry: Number(item.HLP_MAX_CARRY_FORWARD) || 0,
          employeeTypeId: item.HLP_EMPLOYEE_TYPE || '',
          employeeTypeName: employeeTypeName,
          prorata: item.HLP_PRORATA || 'N',
          calculationMethod: item.HLP_CALCULATION_METHOD || 'ANNUAL_ALLOCATION',
          status: item.HLP_ACTIVE_STATUS || 'A',
          effectiveStartDate:
            item.HLP_EFFECTIVE_START_DATE ||
            item.P_EFFECTIVE_START_DATE ||
            item.p_effectiveStartDate ||
            '',
          effectiveEndDate:
            item.HLP_EFFECTIVE_END_DATE ||
            item.P_EFFECTIVE_END_DATE ||
            item.p_effectiveEndDate ||
            '',
        });
      });

      return { groupedPolicies };
    } catch (error) {
      console.error("[fetchLeavePolicies] Error:", error);
      return rejectWithValue(error.message || "Failed to load leave policies.");
    }
  }
);

export const fetchLeaveTypesForPolicy = createAsyncThunk(
  'leavePolicy/fetchLeaveTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await leaveService.GetLeaveTypeList();

      console.log("[fetchLeaveTypesForPolicy] Response:", response);

      if (response?.StatusCode !== 200) {
        throw new Error("Failed to load leave types.");
      }

      const formattedTypes = (response.ResultSet || [])
        .filter((item) => item.HLT_ACTIVE_STATUS === "Active")
        .map((item) => ({
          id: String(item.HLT_TYPE_ID),
          code: item.HLT_CODE,
          name: item.HLT_NAME,
        }));

      return formattedTypes;
    } catch (error) {
      console.error("[fetchLeaveTypesForPolicy] Error:", error);
      return rejectWithValue(error.message || "Failed to load leave types.");
    }
  }
);

export const createLeavePolicy = createAsyncThunk(
  'leavePolicy/create',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await policyService.PostLeavePolicy(payload);

      console.log("[createLeavePolicy] Response:", response);

      if (response?.StatusCode !== 200) {
        throw new Error(response?.Message || "Failed to create leave policy.");
      }

      return response;
    } catch (error) {
      console.error("[createLeavePolicy] Error:", error);
      return rejectWithValue(error.message || "Failed to create leave policy.");
    }
  }
);

export const updateLeavePolicy = createAsyncThunk(
  'leavePolicy/update',
  async ({ policyId, ...payload }, { rejectWithValue }) => {
    try {
      const response = await policyService.UpdateLeavePolicy({
        policyId,
        ...payload,
      });

      console.log("[updateLeavePolicy] Response:", response);

      if (response?.StatusCode !== 200) {
        throw new Error(response?.Message || "Failed to update leave policy.");
      }

      return response;
    } catch (error) {
      console.error("[updateLeavePolicy] Error:", error);
      return rejectWithValue(error.message || "Failed to update leave policy.");
    }
  }
);

// Initial state
const initialState = {
  groupedPolicies: {},
  selectedEmployeeType: null,
  leaveTypes: [],
  loading: false,
  error: null,
  saving: false,
  saveError: null,
  leaveTypesLoading: false,
};

// Slice
const leavePolicySlice = createSlice({
  name: 'leavePolicy',
  initialState,
  reducers: {
    setSelectedEmployeeType: (state, action) => {
      state.selectedEmployeeType = action.payload;
    },
    clearError: (state) => {
      state.error = null;
      state.saveError = null;
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch policies
      .addCase(fetchLeavePolicies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeavePolicies.fulfilled, (state, action) => {
        state.loading = false;
        state.groupedPolicies = action.payload.groupedPolicies;
        state.error = null;
      })
      .addCase(fetchLeavePolicies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load leave policies.";
      })

      // Fetch leave types
      .addCase(fetchLeaveTypesForPolicy.pending, (state) => {
        state.leaveTypesLoading = true;
      })
      .addCase(fetchLeaveTypesForPolicy.fulfilled, (state, action) => {
        state.leaveTypesLoading = false;
        state.leaveTypes = action.payload;
      })
      .addCase(fetchLeaveTypesForPolicy.rejected, (state) => {
        state.leaveTypesLoading = false;
        state.leaveTypes = [];
      })

      // Create
      .addCase(createLeavePolicy.pending, (state) => {
        state.saving = true;
        state.saveError = null;
      })
      .addCase(createLeavePolicy.fulfilled, (state) => {
        state.saving = false;
        state.saveError = null;
      })
      .addCase(createLeavePolicy.rejected, (state, action) => {
        state.saving = false;
        state.saveError = action.payload || "Failed to create leave policy.";
      })

      // Update
      .addCase(updateLeavePolicy.pending, (state) => {
        state.saving = true;
        state.saveError = null;
      })
      .addCase(updateLeavePolicy.fulfilled, (state) => {
        state.saving = false;
        state.saveError = null;
      })
      .addCase(updateLeavePolicy.rejected, (state, action) => {
        state.saving = false;
        state.saveError = action.payload || "Failed to update leave policy.";
      });
  },
});

// Export actions
export const { setSelectedEmployeeType, clearError, resetState } = leavePolicySlice.actions;

// Selectors
export const selectLeavePolicies = (state) => state.leavePolicy.groupedPolicies;
export const selectSelectedEmployeeType = (state) => state.leavePolicy.selectedEmployeeType;
export const selectLeaveTypes = (state) => state.leavePolicy.leaveTypes;
export const selectLeavePolicyLoading = (state) => state.leavePolicy.loading;
export const selectLeavePolicyError = (state) => state.leavePolicy.error;
export const selectLeavePolicySaving = (state) => state.leavePolicy.saving;
export const selectLeavePolicySaveError = (state) => state.leavePolicy.saveError;
export const selectLeaveTypesLoading = (state) => state.leavePolicy.leaveTypesLoading;

export default leavePolicySlice.reducer;